import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import PackRoutingPanel, { ROUTE_SLOTS } from './PackRoutingPanel';

/**
 * Pack Editor
 *
 * Compose form packs from the shared form library:
 * - A form can live in any number of packs (linked, never copied or re-uploaded)
 * - Drag / arrow-reorder sets the pack's print order
 * - Removing a form from a pack, or deleting a pack, NEVER touches the form itself
 */

const PACK_TYPE_LABELS = {
  retail: 'Deal Desk — Retail',
  wholesale: 'Deal Desk — Wholesale',
  titles: 'Titles',
};

// Short labels for "Prints: X" badges derived from the What Prints Where table
const SLOT_BADGES = {
  pack_route_retail_in_state: 'In-State Retail',
  pack_route_retail_out_of_state: 'Out-of-State Retail',
  pack_route_wholesale: 'Wholesale',
  pack_route_titles: 'Titles (default)',
};

const typeOf = (pack) => pack.pack_type || 'retail';
const isReady = (t) => !!(t.blank_file_path || t.blank_file_url);

// Mapped fields travel with the form (they live on the template, not the pack) —
// count from the fields table with a fallback to the legacy JSONB mappings column.
const fieldCount = (t) => {
  const fromTable = Number(t.mapped_fields_count) || 0;
  const legacy = Array.isArray(t.field_mappings) ? t.field_mappings.length : 0;
  return fromTable > 0 ? fromTable : legacy;
};

const PackEditor = ({ onClose }) => {
  const [packs, setPacks] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [selectedPackId, setSelectedPackId] = useState(null);
  const [packTemplates, setPackTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingContents, setLoadingContents] = useState(false);
  const [saving, setSaving] = useState(false);
  // Routing table (what prints where) — fed by the PackRoutingPanel, drives the badges
  const [routes, setRoutes] = useState({});

  const routeBadgesFor = (packId) =>
    ROUTE_SLOTS.filter(s => routes[s.key] === String(packId)).map(s => SLOT_BADGES[s.key]);

  // New pack form
  const [showNewPack, setShowNewPack] = useState(false);
  const [newPackName, setNewPackName] = useState('');
  const [newPackType, setNewPackType] = useState('retail');

  // Add-forms section
  const [addSearch, setAddSearch] = useState('');
  const [addSelection, setAddSelection] = useState(new Set());

  // Drag reorder
  const [dragIndex, setDragIndex] = useState(null);

  const loadPacks = useCallback(async () => {
    const data = await api.getFormPacks();
    setPacks(data || []);
    return data || [];
  }, []);

  const loadLibrary = useCallback(async () => {
    const data = await api.getFormTemplates();
    setAllTemplates(data || []);
  }, []);

  const loadPackContents = useCallback(async (packId) => {
    if (!packId) { setPackTemplates([]); return; }
    setLoadingContents(true);
    try {
      const data = await api.getFormPackTemplates(packId);
      setPackTemplates(data || []);
    } finally {
      setLoadingContents(false);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        const [loadedPacks] = await Promise.all([loadPacks(), loadLibrary()]);
        const first = loadedPacks.find(p => p.is_active) || loadedPacks[0];
        if (first) setSelectedPackId(first.id);
      } catch (err) {
        console.error('Pack editor load failed:', err);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [loadPacks, loadLibrary]);

  useEffect(() => {
    loadPackContents(selectedPackId).catch(err => console.error('Load pack contents failed:', err));
    setAddSelection(new Set());
    setAddSearch('');
  }, [selectedPackId, loadPackContents]);

  const selectedPack = packs.find(p => p.id === selectedPackId);

  const groupedPacks = useMemo(() => {
    const groups = { retail: [], wholesale: [], titles: [] };
    packs.forEach(p => {
      const t = typeOf(p);
      (groups[t] || groups.retail).push(p);
    });
    return groups;
  }, [packs]);

  const inPackIds = useMemo(() => new Set(packTemplates.map(t => t.id)), [packTemplates]);

  const addCandidates = useMemo(() => {
    const q = addSearch.trim().toLowerCase();
    return allTemplates
      .filter(t => !inPackIds.has(t.id))
      .filter(t => !q || (t.name || '').toLowerCase().includes(q));
  }, [allTemplates, inPackIds, addSearch]);

  // ----- Pack actions -----

  const handleCreatePack = async () => {
    if (!newPackName.trim()) { alert('Enter a pack name'); return; }
    try {
      const pack = await api.createFormPack(newPackName.trim(), '', newPackType);
      setShowNewPack(false);
      setNewPackName('');
      await loadPacks();
      setSelectedPackId(pack.id);
    } catch (err) {
      alert('Failed to create pack: ' + err.message);
    }
  };

  const handleRenamePack = async (pack) => {
    const name = window.prompt('Rename pack:', pack.name);
    if (!name || !name.trim() || name.trim() === pack.name) return;
    try {
      await api.updateFormPack(pack.id, { name: name.trim() });
      await loadPacks();
    } catch (err) {
      alert('Failed to rename: ' + err.message);
    }
  };

  const handleDuplicatePack = async (pack) => {
    try {
      const copy = await api.duplicateFormPack(pack.id);
      await loadPacks();
      setSelectedPackId(copy.id);
    } catch (err) {
      alert('Failed to duplicate: ' + err.message);
    }
  };

  // NOTE: the old "Set as Default" / state-scope controls were replaced by the
  // What Prints Where routing table (PackRoutingPanel) — one visible table, no
  // invisible per-group defaults.

  // Pack type moves a pack between the Deal Desk and Titles worlds. This is THE fix for
  // "my pack disappeared": a pack created with the wrong type is invisible in the other
  // module's screens — here it's always visible and one dropdown away from being fixed.
  const handleTypeChange = async (pack, newType) => {
    if (newType === typeOf(pack)) return;
    if (!window.confirm(
      `Move "${pack.name}" to ${PACK_TYPE_LABELS[newType]}?\n\nIt will show up in that module's printing and stop showing in ${PACK_TYPE_LABELS[typeOf(pack)]}. Forms inside it are unaffected.`
    )) return;
    try {
      await api.updateFormPack(pack.id, { pack_type: newType });
      await loadPacks();
    } catch (err) {
      alert('Failed to change type: ' + err.message);
    }
  };

  const handleDeletePack = async (pack) => {
    if (!window.confirm(
      `Delete the pack "${pack.name}"?\n\nOnly the grouping is deleted — every form in it stays in your Form Library and in any other packs. If this pack is assigned in "What Prints Where", that slot is cleared.`
    )) return;
    try {
      await api.deleteFormPack(pack.id);
      const remaining = await loadPacks();
      if (selectedPackId === pack.id) {
        setSelectedPackId(remaining[0]?.id || null);
      }
    } catch (err) {
      alert('Failed to delete pack: ' + err.message);
    }
  };

  // ----- Pack contents actions -----

  const persistOrder = async (ordered) => {
    setSaving(true);
    try {
      await api.reorderPackTemplates(selectedPackId, ordered.map(t => t.id));
    } catch (err) {
      alert('Failed to save order: ' + err.message);
      await loadPackContents(selectedPackId);
    } finally {
      setSaving(false);
    }
  };

  const moveTemplate = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= packTemplates.length) return;
    const next = [...packTemplates];
    [next[index], next[target]] = [next[target], next[index]];
    setPackTemplates(next);
    persistOrder(next);
  };

  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) { setDragIndex(null); return; }
    const next = [...packTemplates];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIndex, 0, moved);
    setPackTemplates(next);
    setDragIndex(null);
    persistOrder(next);
  };

  const handleRemoveFromPack = async (template) => {
    if (!window.confirm(
      `Remove "${template.name}" from this pack?\n\nThe form itself is NOT deleted — it stays in your Form Library and in any other packs it belongs to.`
    )) return;
    try {
      await api.unlinkTemplateFromPack(selectedPackId, template.id);
      await Promise.all([loadPackContents(selectedPackId), loadPacks(), loadLibrary()]);
    } catch (err) {
      alert('Failed to remove: ' + err.message);
    }
  };

  const handleAddSelected = async () => {
    if (addSelection.size === 0) return;
    setSaving(true);
    try {
      await api.linkTemplatesToPack(selectedPackId, Array.from(addSelection));
      setAddSelection(new Set());
      await Promise.all([loadPackContents(selectedPackId), loadPacks(), loadLibrary()]);
    } catch (err) {
      alert('Failed to add forms: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleAddSelection = (id) => {
    setAddSelection(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const packBadges = (template) => {
    const list = Array.isArray(template.packs) ? template.packs : [];
    return list.filter(p => p.id !== selectedPackId);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-5xl rounded-lg shadow-xl flex flex-col" style={{ height: '88vh' }}>
        {/* Header */}
        <div className="px-5 py-3.5 border-b flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="font-semibold text-lg">Pack Editor</h2>
            <p className="text-xs text-gray-500">
              A form can live in many packs off one upload. Removing a form from a pack, or deleting a pack, never deletes the form.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {saving && <span className="text-xs text-blue-600 animate-pulse">Saving…</span>}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none px-2">✕</button>
          </div>
        </div>

        {/* What Prints Where — the single visible routing table */}
        <div className="px-5 py-3 border-b flex-shrink-0">
          <PackRoutingPanel onRoutesChange={setRoutes} />
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">Loading packs…</div>
        ) : (
          <div className="flex-1 flex overflow-hidden min-h-0">

            {/* Left — packs list */}
            <div className="w-64 border-r bg-neutral-50 flex flex-col flex-shrink-0">
              <div className="px-3 py-2 border-b flex items-center justify-between">
                <span className="text-xs font-semibold text-neutral-600">Your Packs</span>
                <button
                  onClick={() => setShowNewPack(v => !v)}
                  className="px-2 py-0.5 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                >
                  + New Pack
                </button>
              </div>

              {showNewPack && (
                <div className="p-3 border-b bg-white space-y-2">
                  <input
                    type="text"
                    value={newPackName}
                    onChange={e => setNewPackName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleCreatePack()}
                    placeholder="Pack name (e.g. Out-of-State Retail)"
                    className="w-full border rounded px-2 py-1.5 text-xs"
                    autoFocus
                  />
                  <select
                    value={newPackType}
                    onChange={e => setNewPackType(e.target.value)}
                    className="w-full border rounded px-2 py-1.5 text-xs"
                  >
                    <option value="retail">{PACK_TYPE_LABELS.retail}</option>
                    <option value="wholesale">{PACK_TYPE_LABELS.wholesale}</option>
                    <option value="titles">{PACK_TYPE_LABELS.titles}</option>
                  </select>
                  <div className="flex gap-1.5">
                    <button onClick={handleCreatePack} className="flex-1 px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700">Create</button>
                    <button onClick={() => setShowNewPack(false)} className="px-2 py-1 text-xs text-neutral-500 hover:text-neutral-700">Cancel</button>
                  </div>
                </div>
              )}

              <div className="flex-1 overflow-y-auto p-2 space-y-3">
                {['retail', 'wholesale', 'titles'].map(type => (
                  groupedPacks[type].length > 0 && (
                    <div key={type}>
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-neutral-400 px-1 mb-1">
                        {PACK_TYPE_LABELS[type]}
                      </div>
                      <div className="space-y-1">
                        {groupedPacks[type].map(pack => (
                          <div
                            key={pack.id}
                            onClick={() => setSelectedPackId(pack.id)}
                            className={`p-2 rounded border cursor-pointer transition-all ${
                              selectedPackId === pack.id ? 'border-blue-500 bg-blue-50' : 'border-neutral-200 bg-white hover:border-neutral-300'
                            }`}
                          >
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium flex-1 truncate">{pack.name}</span>
                            </div>
                            <div className="flex items-center gap-1 flex-wrap mt-0.5">
                              <span className="text-[10px] text-neutral-400">{pack.template_count} form{Number(pack.template_count) === 1 ? '' : 's'}</span>
                              {routeBadgesFor(pack.id).map(b => (
                                <span key={b} className="text-[9px] bg-green-100 text-green-700 px-1 py-0.5 rounded font-semibold whitespace-nowrap">Prints: {b}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                ))}
                {packs.length === 0 && (
                  <p className="text-xs text-center text-neutral-400 py-6">No packs yet.<br/>Create one to get started.</p>
                )}
              </div>
            </div>

            {/* Right — selected pack */}
            <div className="flex-1 flex flex-col overflow-hidden min-w-0">
              {!selectedPack ? (
                <div className="flex-1 flex items-center justify-center text-neutral-400 text-sm">Select a pack on the left</div>
              ) : (
                <>
                  {/* Pack toolbar */}
                  <div className="px-4 py-2.5 border-b flex items-center justify-between flex-shrink-0 bg-white">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold truncate">{selectedPack.name}</h3>
                        <select
                          value={typeOf(selectedPack)}
                          onChange={e => handleTypeChange(selectedPack, e.target.value)}
                          title="Which module this pack belongs to — change it if a pack 'disappeared' into the wrong world"
                          className="text-[10px] border border-neutral-200 rounded px-1 py-0.5 text-neutral-600 bg-white"
                        >
                          <option value="retail">{PACK_TYPE_LABELS.retail}</option>
                          <option value="wholesale">{PACK_TYPE_LABELS.wholesale}</option>
                          <option value="titles">{PACK_TYPE_LABELS.titles}</option>
                        </select>
                        {routeBadgesFor(selectedPack.id).map(b => (
                          <span key={b} className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">Prints: {b}</span>
                        ))}
                      </div>
                      <p className="text-[11px] text-neutral-400">Forms print in this order, top to bottom. Assign when this pack prints in the table above.</p>
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <button onClick={() => handleRenamePack(selectedPack)} className="px-2.5 py-1 text-xs border rounded hover:bg-neutral-50">Rename</button>
                      <button onClick={() => handleDuplicatePack(selectedPack)} className="px-2.5 py-1 text-xs border rounded hover:bg-neutral-50">Duplicate</button>
                      <button
                        onClick={() => handleDeletePack(selectedPack)}
                        title="Deletes the grouping only — forms are kept"
                        className="px-2.5 py-1 text-xs border border-red-200 text-red-600 rounded hover:bg-red-50"
                      >
                        Delete Pack
                      </button>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto">
                    {/* Pack contents */}
                    <div className="p-4">
                      <div className="text-xs font-semibold text-neutral-600 mb-2">In this pack ({packTemplates.length})</div>
                      {loadingContents ? (
                        <p className="text-xs text-neutral-400 py-4 text-center">Loading…</p>
                      ) : packTemplates.length === 0 ? (
                        <p className="text-xs text-neutral-400 py-4 text-center border border-dashed rounded-lg">
                          Nothing in this pack yet — add forms from the library below.
                        </p>
                      ) : (
                        <div className="space-y-1">
                          {packTemplates.map((t, index) => (
                            <div
                              key={t.id}
                              draggable
                              onDragStart={() => setDragIndex(index)}
                              onDragOver={e => e.preventDefault()}
                              onDrop={() => handleDrop(index)}
                              onDragEnd={() => setDragIndex(null)}
                              className={`flex items-center gap-2 p-2 rounded border bg-white ${
                                dragIndex === index ? 'border-blue-400 opacity-60' : 'border-neutral-200'
                              }`}
                            >
                              <span className="cursor-grab text-neutral-300 select-none" title="Drag to reorder">⠿</span>
                              <span className="w-5 text-center text-[10px] text-neutral-400 font-mono">{index + 1}</span>
                              <span className="flex-1 text-sm font-medium truncate">{t.name}</span>
                              {isReady(t) ? (
                                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded whitespace-nowrap">Ready</span>
                              ) : (
                                <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded whitespace-nowrap" title="No print-ready file — this form will not print until it's set up">Needs setup</span>
                              )}
                              <div className="flex flex-col">
                                <button onClick={() => moveTemplate(index, -1)} disabled={index === 0} className="text-neutral-400 hover:text-neutral-700 disabled:opacity-20 text-[10px] leading-none px-1">▲</button>
                                <button onClick={() => moveTemplate(index, 1)} disabled={index === packTemplates.length - 1} className="text-neutral-400 hover:text-neutral-700 disabled:opacity-20 text-[10px] leading-none px-1">▼</button>
                              </div>
                              <button
                                onClick={() => handleRemoveFromPack(t)}
                                title="Remove from this pack (the form is kept in the library)"
                                className="text-red-400 hover:text-red-600 text-sm px-1"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Add forms from library */}
                    <div className="px-4 pb-4">
                      <div className="border-t pt-3">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-semibold text-neutral-600">Add forms from your library</div>
                          <button
                            onClick={handleAddSelected}
                            disabled={addSelection.size === 0 || saving}
                            className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-40"
                          >
                            Add {addSelection.size > 0 ? `${addSelection.size} ` : ''}Selected
                          </button>
                        </div>
                        <input
                          type="text"
                          value={addSearch}
                          onChange={e => setAddSearch(e.target.value)}
                          placeholder="Search forms…"
                          className="w-full border rounded px-2.5 py-1.5 text-xs mb-2"
                        />
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {addCandidates.length === 0 ? (
                            <p className="text-xs text-neutral-400 py-3 text-center">
                              {addSearch ? 'No forms match your search.' : 'Every form in your library is already in this pack.'}
                            </p>
                          ) : addCandidates.map(t => (
                            <label key={t.id} className="flex items-center gap-2.5 p-2 rounded border border-neutral-200 hover:bg-neutral-50 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={addSelection.has(t.id)}
                                onChange={() => toggleAddSelection(t.id)}
                              />
                              <span className="flex-1 text-sm truncate">
                                {t.name}
                                {t.is_active === false && <span className="ml-1.5 text-[10px] text-neutral-400">(archived)</span>}
                              </span>
                              {fieldCount(t) > 0 && (
                                <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded whitespace-nowrap" title="Auto-fill fields already mapped on this form — they carry over when you add it to a pack">
                                  {fieldCount(t)} fields
                                </span>
                              )}
                              {packBadges(t).map(p => (
                                <span key={p.id} className="text-[9px] bg-neutral-100 text-neutral-500 px-1 py-0.5 rounded whitespace-nowrap">{p.name}</span>
                              ))}
                              {isReady(t) ? (
                                <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded whitespace-nowrap">Ready</span>
                              ) : (
                                <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded whitespace-nowrap">Needs setup</span>
                              )}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackEditor;
